import type { Document, DocumentListResponse } from '../types/document';
import type { Extraction } from '../types/extraction';
import { authFetch, handleSessionExpired } from './authService';

const API_BASE = `${import.meta.env.VITE_API_URL || 'http://localhost:8001'}/api/v1`;

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    // authFetch a déjà tenté le refresh — session réellement expirée.
    handleSessionExpired(response);
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Erreur réseau' }));

    // Handle validation errors (detail is an object with message and errors array of strings)
    if (typeof error.detail === 'object' && error.detail.message) {
      const validationErrors = error.detail.errors || [];
      const fullMessage = error.detail.message +
        (validationErrors.length > 0 ? ':\n• ' + validationErrors.join('\n• ') : '');
      const wrapped = new Error(fullMessage) as Error & { detail?: any; status?: number };
      wrapped.detail = error.detail;
      wrapped.status = response.status;
      throw wrapped;
    }

    const wrapped = new Error(
      typeof error.detail === 'string' ? error.detail : 'Erreur réseau',
    ) as Error & { detail?: any; status?: number };
    wrapped.detail = error.detail;
    wrapped.status = response.status;
    throw wrapped;
  }

  return response.json();
}

/** PROD1.10 #670 — candidat de doublon de contenu renvoyé dans le 409 de /ingest. */
export interface DuplicateCandidate {
  id: string;
  original_filename: string;
  status: string;
  version: number | null;
}

/** Options de gestion d'un doublon de contenu sur /ingest (PROD1.10 #670). */
export interface UploadDuplicateOptions {
  /** Seule valeur acceptée par le backend : 'reextract'. */
  onDuplicate?: 'reextract';
  /** Document cible sur lequel relancer la ré-extraction versionnée. */
  targetDocumentId?: string;
}

export async function uploadDocument(
  file: File,
  domain: string = 'acte_vente',
  options: UploadDuplicateOptions = {},
): Promise<Document> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('domain', domain);
  if (options.onDuplicate) {
    formData.append('on_duplicate', options.onDuplicate);
  }
  if (options.targetDocumentId) {
    formData.append('target_document_id', options.targetDocumentId);
  }

  const uploadResponse = await authFetch(`${API_BASE}/documents/ingest`, {
    method: 'POST',
    body: formData,
  });

  // handleResponse rejette sur !ok en attachant `detail` + `status` à l'erreur.
  // Le 409 « doublon de contenu » remonte donc tel quel (detail.candidates
  // exploitable côté UI) : c'est l'appelant qui décide quoi en faire.
  const document = await handleResponse<Document>(uploadResponse);

  // Return immediately with uploaded document (status='uploaded')
  // User will manually trigger extraction when ready using the "Extraire avec LLM" button
  return document;
}

export type SortField =
  | 'filename' | 'domain' | 'status'
  | 'created_at' | 'extracted_at' | 'validated_at' | 'last_action_at'
  | 'schema_version' | 'prompt_version' | 'llm_version' | 'ocr_version';
export type SortOrder = 'asc' | 'desc';

export interface GetDocumentsParams {
  page?: number;
  pageSize?: number;
  status?: string;
  domain?: string;
  dateFrom?: string;   // ISO 8601
  dateTo?: string;     // ISO 8601
  tags?: string[];
  filenameSearch?: string;
  /** Multi-value substring match across uploader/extractor/validator (OR). */
  people?: string[];
  schemaVersion?: string[];
  promptVersion?: string[];
  llmVersion?: string[];
  ocrVersion?: string[];
  /** PROD1.7.8 N2 — filter by LLM flags on current extraction (any-match). */
  flags?: string[];
  /** PROD1.7.8 N3 — filter by confidence bucket (weak/attention/good/excellent/unknown ; any-match). */
  confidenceLevel?: string[];
  sortBy?: SortField;
  sortOrder?: SortOrder;
  signal?: AbortSignal;
}

export async function getDocuments(params: GetDocumentsParams = {}): Promise<DocumentListResponse> {
  const {
    page = 1,
    pageSize = 20,
    status,
    domain,
    dateFrom,
    dateTo,
    tags,
    filenameSearch,
    people,
    schemaVersion,
    promptVersion,
    llmVersion,
    ocrVersion,
    flags,
    confidenceLevel,
    sortBy = 'created_at',
    sortOrder = 'desc',
    signal
  } = params;

  const queryParams = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
    sort_by: sortBy,
    sort_order: sortOrder,
  });

  if (status) queryParams.append('status', status);
  if (domain) queryParams.append('domain', domain);
  if (dateFrom) queryParams.append('date_from', dateFrom);
  if (dateTo) queryParams.append('date_to', dateTo);
  if (tags && tags.length > 0) {
    tags.forEach((t) => queryParams.append('tags', t));
  }
  if (filenameSearch) queryParams.append('filename_search', filenameSearch);
  people?.forEach((p) => queryParams.append('people', p));
  schemaVersion?.forEach((v) => queryParams.append('schema_version', v));
  promptVersion?.forEach((v) => queryParams.append('prompt_version', v));
  llmVersion?.forEach((v) => queryParams.append('llm_version', v));
  ocrVersion?.forEach((v) => queryParams.append('ocr_version', v));
  flags?.forEach((f) => queryParams.append('flags', f));
  confidenceLevel?.forEach((c) => queryParams.append('confidence_level', c));

  const response = await authFetch(
    `${API_BASE}/documents?${queryParams.toString()}`,
    { signal }
  );

  return handleResponse<DocumentListResponse>(response);
}

export interface DocumentFilterFacets {
  domains: string[];
  tags: string[];
  /** PROD1.7.8 N2 — flags LLM apparaissant sur les extractions courantes du tenant. */
  flags: string[];
  schema_versions: string[];
  prompt_versions: string[];
  llm_versions: string[];
  ocr_versions: string[];
  people: string[];
}

export async function getDocumentFilterFacets(signal?: AbortSignal): Promise<DocumentFilterFacets> {
  const response = await authFetch(`${API_BASE}/documents/filter-facets`, { signal });
  return handleResponse<DocumentFilterFacets>(response);
}

export interface DocumentsStats {
  to_validate: number;
  pending_validation: number;
  in_progress: number;
  validated_last_7d: number;
  in_error: number;
}

export async function getDocumentsStats(signal?: AbortSignal): Promise<DocumentsStats> {
  const response = await authFetch(`${API_BASE}/documents/stats`, { signal });
  return handleResponse<DocumentsStats>(response);
}

export async function processDocument(id: string): Promise<Document> {
  const response = await authFetch(`${API_BASE}/documents/${id}/process`, {
    method: 'POST',
  });
  return handleResponse<Document>(response);
}

export async function getDocumentDistinctTags(domain?: string): Promise<string[]> {
  const params = domain ? `?domain=${encodeURIComponent(domain)}` : '';
  const response = await authFetch(`${API_BASE}/documents/tags/distinct${params}`);
  return handleResponse<string[]>(response);
}

export async function updateDocumentTags(documentId: string, tags: string[]): Promise<{ id: string; tags: string[] }> {
  const response = await authFetch(`${API_BASE}/documents/${documentId}/tags`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tags }),
  });
  return handleResponse<{ id: string; tags: string[] }>(response);
}

export async function getDocument(id: string, signal?: AbortSignal): Promise<Document> {
  const response = await authFetch(`${API_BASE}/documents/${id}`, { signal });
  return handleResponse<Document>(response);
}

export async function getDocumentText(id: string): Promise<{
  document_id: string;
  page_count: number;
  ocr_used: boolean;
  text: string;
}> {
  const response = await authFetch(`${API_BASE}/documents/${id}/text`);
  return handleResponse(response);
}

// Jeton ephemere pour streamer le PDF : pdf.js charge le document page par page
// (requetes Range) et ne peut pas injecter d'en-tete Authorization, donc l'URL
// doit etre auto-portante. On demande un jeton scope-document (30 min) via
// authFetch, puis on le passe en ?ftk= dans la fileUrl du viewer. Le viewer
// renouvelle ce jeton avant expiration (cf. PdfViewerImpl).
export interface FileTokenInfo {
  token: string;
  expiresIn: number; // secondes
}

export async function getDocumentFileToken(id: string): Promise<FileTokenInfo> {
  const response = await authFetch(`${API_BASE}/documents/${id}/file-token`);
  if (!response.ok) {
    throw new Error('Failed to fetch document file token');
  }
  const data = await response.json();
  return { token: data.token, expiresIn: data.expires_in };
}

// Construit l'URL de streaming directe (sans bearer) pour pdf.js.
export function buildDocumentFileUrl(id: string, token: string): string {
  return `${API_BASE}/documents/${id}/file?ftk=${encodeURIComponent(token)}`;
}

export async function triggerExtraction(id: string): Promise<Document> {
  const response = await authFetch(`${API_BASE}/documents/${id}/retry`, {
    method: 'POST',
  });
  return handleResponse<Document>(response);
}

export async function retryDocument(
  id: string,
  body?: { prompt_id?: string; schema_id?: string }
): Promise<Document> {
  const response = await authFetch(`${API_BASE}/documents/${id}/retry`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  return handleResponse<Document>(response);
}

// PROD1.10 #667 — Rouvrir un document validé : crée une v+1 éditable
// (source=human, sans LLM). Le document repasse « À valider ».
export async function createVersion(documentId: string): Promise<Document> {
  const response = await authFetch(
    `${API_BASE}/documents/${documentId}/create-version`,
    { method: 'POST' }
  );
  return handleResponse<Document>(response);
}

export async function getDocumentExtractions(
  documentId: string,
  signal?: AbortSignal
): Promise<Extraction[]> {
  const response = await authFetch(
    `${API_BASE}/documents/${documentId}/extractions`,
    { signal }
  );
  return handleResponse<Extraction[]>(response);
}

export async function getExtraction(id: string): Promise<Extraction> {
  const response = await authFetch(`${API_BASE}/documents/${id}/extraction`);

  if (!response.ok && response.status === 404) {
    throw new Error('NOT_FOUND');
  }

  return handleResponse<Extraction>(response);
}

export async function getCurrentExtraction(documentId: string, signal?: AbortSignal): Promise<Extraction> {
  const response = await authFetch(`${API_BASE}/documents/${documentId}/extractions/current`, { signal });
  return handleResponse<Extraction>(response);
}

export async function getProvenance(extractionId: string, signal?: AbortSignal): Promise<{
  extraction_id: string;
  fields: Array<{
    field_path: string;
    text_snippet: string;
    page_number: number;
    bbox?: { x1: number; y1: number; x2: number; y2: number } | null;
  }>;
}> {
  const response = await authFetch(`${API_BASE}/extractions/${extractionId}/provenance`, { signal });
  return handleResponse(response);
}

export async function patchExtractionDraft(
  documentId: string,
  payload: any,
  forceOverwrite: boolean = false,
): Promise<void> {
  const response = await authFetch(`${API_BASE}/documents/${documentId}/draft`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ payload, force_overwrite: forceOverwrite }),
  });
  if (!response.ok && response.status !== 204) {
    return handleResponse(response);
  }
}


export async function getExtractionQueuePauseState(): Promise<{ paused: boolean }> {
  const response = await authFetch(`${API_BASE}/extractions/queue-pause`);
  return handleResponse(response);
}


export async function validateDocument(
  documentId: string,
  extractionData: any,
  extractionIdExpected?: string | null,
): Promise<{
  extraction_id: string;
  version: number;
  source: string;
  validated_at: string;
}> {
  const body: Record<string, any> = { extraction_data: extractionData };
  if (extractionIdExpected) {
    body.extraction_id_expected = extractionIdExpected;
  }
  const response = await authFetch(`${API_BASE}/documents/${documentId}/validate`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return handleResponse(response);
}
